import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Square, Sparkles, Zap, Shield, ArrowRight, CheckCircle2, TrendingUp, Maximize2 } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

// Animated counter hook
const useAnimatedCounter = (target, duration = 1000, trigger = true) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!trigger) return;
    
    const numericTarget = parseFloat(target.toString().replace(/[^0-9.]/g, ''));
    const steps = 30;
    const increment = numericTarget / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= numericTarget) {
        setCount(numericTarget);
        clearInterval(timer);
      } else {
        setCount(Math.round(current * 10) / 10);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target, duration, trigger]);

  return count;
};

// Animated stat component
const AnimatedStat = ({ num, value, label, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
  const suffix = value.replace(/[0-9.]/g, '');
  const animatedValue = useAnimatedCounter(numericValue, 800, isVisible);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 10 }}
      transition={{ duration: 0.3 }}
      style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        gap: '10px',
        padding: '8px',
        background: 'white',
        border: '1px solid #bbb',
        borderRadius: '2px'
      }}
      whileHover={{ scale: 1.02, boxShadow: '0 2px 8px rgba(0,0,128,0.15)' }}
    >
      <div style={{ 
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000080',
        color: 'white',
        fontFamily: "'Segoe UI', sans-serif",
        fontSize: '10px',
        fontWeight: 'bold',
        borderRadius: '2px'
      }}>
        {num}
      </div>
      <div>
        <div style={{ 
          fontSize: '18px', 
          fontWeight: 'bold', 
          color: '#000080',
          fontFamily: "'Segoe UI', sans-serif",
          lineHeight: 1
        }}>
          {Math.round(animatedValue * 10) / 10}{suffix}
        </div>
        <div style={{ 
          fontSize: '10px', 
          color: '#666',
          fontFamily: "'Segoe UI', sans-serif",
          marginTop: '2px'
        }}>
          {label}
        </div>
      </div>
    </motion.div>
  );
};

// Typing animation component
const TypeWriter = ({ text, speed = 30, delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [started, setStarted] = useState(false);
  
  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimer);
  }, [delay]);
  
  useEffect(() => {
    if (!started) return;
    
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayText(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
      }
    }, speed);
    
    return () => clearInterval(timer);
  }, [text, speed, started]);
  
  return (
    <span>
      {displayText}
      {displayText.length < text.length && (
        <span style={{ 
          animation: 'blink 0.7s infinite',
          marginLeft: '2px'
        }}>|</span>
      )}
    </span>
  );
};

export default function WelcomeWindow({ 
  zIndex, 
  onClose, 
  onMinimize, 
  onFocus,
  position = { x: 180, y: 50 },
  onPositionChange,
  onOpenContact,
  onOpenServices
}) {
  const [isMaximized, setIsMaximized] = useState(false);
  const [currentTab, setCurrentTab] = useState('welcome');
  const [hoveredService, setHoveredService] = useState(null);
  const [size, setSize] = useState({ width: 650, height: 520 });
  const [isResizing, setIsResizing] = useState(false);
  const windowRef = useRef(null);

  // Resize handler
  const handleResizeStart = (e, direction) => {
    if (isMaximized) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;
    const startPosX = position.x;
    const startPosY = position.y;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startPosX;
      let newY = startPosY;

      if (direction.includes('e')) {
        newWidth = Math.max(450, startWidth + deltaX);
      }
      if (direction.includes('w')) {
        const widthDelta = Math.min(deltaX, startWidth - 450);
        newWidth = startWidth - widthDelta;
        newX = startPosX + widthDelta;
      }
      if (direction.includes('s')) {
        newHeight = Math.max(350, startHeight + deltaY);
      }
      if (direction.includes('n')) {
        const heightDelta = Math.min(deltaY, startHeight - 350);
        newHeight = startHeight - heightDelta;
        newY = startPosY + heightDelta;
      }

      setSize({ width: newWidth, height: newHeight });
      if (direction.includes('w') || direction.includes('n')) {
        onPositionChange?.({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const windowStyle = isMaximized
    ? { zIndex, left: 0, top: 0, width: '100%', height: 'calc(100vh - 36px)' }
    : { zIndex, left: position.x, top: position.y, width: size.width, height: size.height };

  const tabs = [
    { id: 'welcome', label: 'Velkommen', icon: Sparkles },
    { id: 'services', label: 'Hva Vi Gjør', icon: Zap },
    { id: 'why', label: 'Hvorfor Oss', icon: Shield },
  ];

  const services = [
    { 
      title: 'Agentiske AI-systemer', 
      desc: 'Agenter som forsker, beslutter og utfører. Ingen håndholding nødvendig.',
      icon: '🤖'
    },
    { 
      title: 'AI-strategi', 
      desc: 'Dropp 200-siders rapporter. Få en veikart som er i produksjon innen 90 dager.',
      icon: '🎯'
    },
    { 
      title: 'Prosessautomatisering', 
      desc: 'Gjør manuelle arbeidsflyter til intelligente pipelines som skalerer.',
      icon: '⚡'
    },
    { 
      title: 'Dataintelligens', 
      desc: 'Trekk ut innsikt som driver beslutninger, ikke bare dashboards.',
      icon: '📊'
    },
  ];

  const whyUsPoints = [
    { 
      title: 'Vi sier hva vi skal gjøre, så gjør vi det.', 
      desc: 'Ingen scope creep. Ingen overraskelsesfakturaer. Ingen "vi trenger mer data."' 
    },
    { 
      title: 'Vi eier resultatet.', 
      desc: '99.7% oppetid eller vi fikser det gratis. Det er vår garanti.' 
    },
    { 
      title: 'Vi leverer raskt.', 
      desc: 'Uker til produksjon, ikke kvartaler. Ekte AI, ekte resultater.' 
    },
  ];

  return (
    <motion.div
      ref={windowRef}
      className="win-window"
      style={{ ...windowStyle, position: 'fixed', display: 'flex', flexDirection: 'column' }}
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      onClick={onFocus}
      drag={!isMaximized && !isResizing}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={{ left: -400, right: 1200, top: 0, bottom: 600 }}
      onDragEnd={(event, info) => {
        if (!isMaximized && !isResizing) {
          onPositionChange?.({
            x: Math.max(0, position.x + info.offset.x),
            y: Math.max(0, position.y + info.offset.y)
          });
        }
      }}
    >
      {/* Title Bar */}
      <div className="win-window-header" style={{ cursor: isMaximized ? 'default' : 'grab' }}>
        <div className="win-window-title">
          <motion.div 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              width: '14px', 
              height: '14px', 
              gap: '1px',
              marginRight: '6px'
            }}
            animate={{ rotate: [0, 0, 360] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
          >
            <div style={{ background: '#ff0000' }} />
            <div style={{ background: '#00ff00' }} />
            <div style={{ background: '#0000ff' }} />
            <div style={{ background: '#ffff00' }} />
          </motion.div>
          Velkommen til Autonomous
        </div>
        <div className="win-window-controls">
          <button className="win-ctrl-btn" onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.click); onMinimize(); }}>
            <Minus size={10} />
          </button>
          <button className="win-ctrl-btn" onClick={(e) => { e.stopPropagation(); setIsMaximized(!isMaximized); }}>
            <Square size={8} />
          </button>
          <button className="win-ctrl-btn win-close" onClick={(e) => { e.stopPropagation(); playSound(SOUNDS.windowClose); onClose(); }}>
            <X size={10} />
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ 
        display: 'flex', 
        background: '#c0c0c0',
        borderBottom: '1px solid #808080',
        padding: '0 8px'
      }}>
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <motion.button
              key={tab.id}
              onClick={() => { playSound(SOUNDS.click); setCurrentTab(tab.id); }}
              whileHover={{ backgroundColor: '#d4d4d4' }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '8px 16px',
                background: currentTab === tab.id ? 'white' : 'transparent',
                border: currentTab === tab.id ? '1px solid #808080' : '1px solid transparent',
                borderBottom: currentTab === tab.id ? '1px solid white' : '1px solid transparent',
                marginBottom: '-1px',
                cursor: 'pointer',
                fontFamily: "'Segoe UI', sans-serif",
                fontSize: '12px',
                fontWeight: currentTab === tab.id ? 'bold' : 'normal',
                color: currentTab === tab.id ? '#000080' : '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '3px 3px 0 0'
              }}
            >
              <TabIcon size={12} />
              {tab.label}
            </motion.button>
          );
        })}
      </div>

      {/* Content Area */}
      <div style={{ 
        flex: 1,
        background: 'white', 
        overflow: 'auto',
        minHeight: 0
      }}>
        <AnimatePresence mode="wait">
          {/* Welcome Tab */}
          {currentTab === 'welcome' && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              style={{ padding: '20px', height: '100%', boxSizing: 'border-box' }}
            >
              {/* Hero Section */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', alignItems: 'flex-start' }}>
                {/* Windows Flag */}
                <motion.div 
                  style={{
                    width: '64px',
                    height: '64px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '3px',
                    flexShrink: 0
                  }}
                  animate={{ 
                    boxShadow: ['0 0 0 rgba(0,0,128,0)', '0 0 20px rgba(0,0,128,0.3)', '0 0 0 rgba(0,0,128,0)']
                  }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <motion.div 
                    style={{ background: '#ff0000', borderRadius: '2px' }}
                    whileHover={{ scale: 1.1 }}
                  />
                  <motion.div 
                    style={{ background: '#00ff00', borderRadius: '2px' }}
                    whileHover={{ scale: 1.1 }}
                  />
                  <motion.div 
                    style={{ background: '#0000ff', borderRadius: '2px' }}
                    whileHover={{ scale: 1.1 }}
                  />
                  <motion.div 
                    style={{ background: '#ffff00', borderRadius: '2px' }}
                    whileHover={{ scale: 1.1 }}
                  />
                </motion.div>

                <div>
                  <h1 style={{ 
                    fontSize: '22px', 
                    fontWeight: 'bold', 
                    color: '#000080',
                    margin: '0 0 8px 0',
                    fontFamily: "'Segoe UI', sans-serif"
                  }}>
                    <TypeWriter text="Vi bygger AI som fungerer." speed={40} />
                  </h1>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#333', 
                    margin: 0,
                    fontFamily: "'Segoe UI', sans-serif",
                    lineHeight: 1.6
                  }}>
                    Ikke demoer. Ikke prototyper. <strong>Produksjonssystemer</strong> som kjører autonomt og leverer ekte resultater.
                  </p>
                </div>
              </div>

              {/* Animated Stats */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: '24px',
                padding: '16px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}>
                <AnimatedStat num="01" value="6 uker" label="til produksjon" delay={200} />
                <AnimatedStat num="02" value="99.7%" label="oppetid garantert" delay={400} />
                <AnimatedStat num="03" value="0" label="feilede piloter" delay={600} />
              </div>

              {/* CTA Section */}
              <div style={{ 
                display: 'flex', 
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                background: '#f0f4ff',
                border: '1px solid #b8c5e8',
                borderRadius: '4px'
              }}>
                <div>
                  <p style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: '#000080',
                    fontFamily: "'Segoe UI', sans-serif"
                  }}>
                    Klar for å snakke?
                  </p>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '11px',
                    color: '#555',
                    fontFamily: "'Segoe UI', sans-serif"
                  }}>
                    La oss diskutere prosjektet ditt — ingen forpliktelser.
                  </p>
                </div>
                <motion.button 
                  onClick={() => { playSound(SOUNDS.click); onOpenContact?.(); onClose(); }}
                  whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0,0,128,0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '10px 20px',
                    background: '#000080',
                    color: 'white',
                    border: '2px solid',
                    borderColor: '#4040ff #000040 #000040 #4040ff',
                    cursor: 'pointer',
                    fontFamily: "'Segoe UI', sans-serif",
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Kontakt Oss
                  <ArrowRight size={14} />
                </motion.button>
              </div>

              {/* Footer */}
              <p style={{ 
                fontSize: '10px', 
                color: '#888',
                fontFamily: "'Segoe UI', sans-serif",
                marginTop: '16px',
                textAlign: 'center'
              }}>
                <strong>Autonomous</strong> — Utforsk skrivebordet for mer!
              </p>
            </motion.div>
          )}

          {/* Services Tab */}
          {currentTab === 'services' && (
            <motion.div
              key="services"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              style={{ padding: '24px' }}
            >
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#000080',
                margin: '0 0 8px 0',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Hva Vi Bygger
              </h2>
              <p style={{ 
                fontSize: '12px', 
                color: '#666',
                margin: '0 0 20px 0',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Fire kjernefunksjoner. Uendelige muligheter.
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {services.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    whileHover={{ 
                      scale: 1.02, 
                      boxShadow: '0 4px 12px rgba(0,0,128,0.15)',
                      borderColor: '#000080'
                    }}
                    onHoverStart={() => setHoveredService(i)}
                    onHoverEnd={() => setHoveredService(null)}
                    style={{ 
                      padding: '16px',
                      background: hoveredService === i ? '#f8f9ff' : '#fafafa',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ 
                      fontSize: '20px', 
                      marginBottom: '8px'
                    }}>
                      {item.icon}
                    </div>
                    <strong style={{ 
                      fontSize: '13px', 
                      color: '#000080',
                      display: 'block',
                      marginBottom: '6px',
                      fontFamily: "'Segoe UI', sans-serif"
                    }}>
                      {item.title}
                    </strong>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '11px', 
                      color: '#555',
                      fontFamily: "'Segoe UI', sans-serif",
                      lineHeight: 1.5
                    }}>
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>

              <motion.button 
                onClick={() => { playSound(SOUNDS.click); onOpenServices?.(); }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  marginTop: '20px',
                  padding: '12px',
                  background: '#c0c0c0',
                  border: '2px solid',
                  borderColor: '#fff #404040 #404040 #fff',
                  cursor: 'pointer',
                  fontFamily: "'Segoe UI', sans-serif",
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Zap size={14} />
                Se Alle Detaljer
              </motion.button>
            </motion.div>
          )}

          {/* Why Us Tab */}
          {currentTab === 'why' && (
            <motion.div
              key="why"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              style={{ padding: '24px' }}
            >
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#000080',
                margin: '0 0 6px 0',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Hvorfor Autonomous?
              </h2>
              
              <p style={{ 
                fontSize: '13px', 
                color: '#333',
                marginBottom: '20px',
                fontFamily: "'Segoe UI', sans-serif",
                lineHeight: 1.5
              }}>
                Vi er ikke enda et AI-konsulentselskap. <strong>Vi er ingeniører som leverer.</strong>
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                {whyUsPoints.map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.15 }}
                    whileHover={{ x: 4 }}
                    style={{ 
                      display: 'flex', 
                      gap: '12px',
                      padding: '14px',
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      borderLeft: '4px solid #000080'
                    }}
                  >
                    <CheckCircle2 size={20} color="#008000" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <strong style={{ 
                        fontSize: '13px', 
                        color: '#000',
                        display: 'block',
                        fontFamily: "'Segoe UI', sans-serif"
                      }}>
                        {item.title}
                      </strong>
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '11px', 
                        color: '#555',
                        fontFamily: "'Segoe UI', sans-serif"
                      }}>
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Box */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                style={{ 
                  padding: '20px',
                  background: 'linear-gradient(135deg, #000080 0%, #1a1a8a 100%)',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}
              >
                <p style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '14px',
                  color: 'white',
                  fontFamily: "'Segoe UI', sans-serif"
                }}>
                  Klar til å bygge noe som <strong>faktisk fungerer</strong>?
                </p>
                <motion.button 
                  onClick={() => { playSound(SOUNDS.click); onOpenContact?.(); onClose(); }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '12px 28px',
                    background: 'white',
                    color: '#000080',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: "'Segoe UI', sans-serif",
                    fontSize: '13px',
                    fontWeight: 'bold',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  La Oss Snakke
                  <ArrowRight size={16} />
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Resize Handles */}
      {!isMaximized && (
        <>
          <div className="resize-handle resize-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
          <div className="resize-handle resize-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
          <div className="resize-handle resize-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
          <div className="resize-handle resize-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
          <div className="resize-handle resize-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
          <div className="resize-handle resize-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
          <div className="resize-handle resize-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
          <div className="resize-handle resize-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
        </>
      )}
    </motion.div>
  );
}
